import React, { useState, useRef, useEffect } from "react";
import { 
  X, 
  Sparkles, 
  Sliders, 
  Plus, 
  Trash2, 
  Check, 
  Settings, 
  Layers, 
  Type, 
  Image as ImageIcon, 
  ArrowLeft, 
  AlignCenter, 
  AlignLeft, 
  AlignRight, 
  Loader2,
  Lock,
  Box,
  Download,
  ChevronDown,
  ChevronUp,
  Palette,
  LayoutTemplate
} from "lucide-react";
import { motion } from "motion/react";
import { AutoFitText, ListItemsEditor, parseOverlayTextToBoxes } from "./TemplateBakerLab";

interface SlideLayoutEditorModalProps {
  slide: any;
  onClose: () => void;
  onSave: (layout: any) => Promise<void>;
  onRegenArtwork: (layout: any) => Promise<void>;
  brandBlueprints: any[];
  brand: any;
  postFormat: string;
  loadingSlideId: string | null;
  totalSlides?: number;
}

export const SlideLayoutEditorModal: React.FC<SlideLayoutEditorModalProps> = ({
  slide,
  onClose,
  onSave,
  onRegenArtwork,
  brandBlueprints = [],
  brand,
  postFormat,
  loadingSlideId,
  totalSlides
}) => {
  const is916 = postFormat === "story";
  const [activeTab, setActiveTab] = useState<"boxes" | "layout">("boxes");
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  // Dragging states
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingLogo, setDraggingLogo] = useState<{
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);
  const [draggingPlaque, setDraggingPlaque] = useState<{
    offsetX: number;
    offsetY: number;
    type: "move" | "resize";
  } | null>(null);

  const [snapGuide, setSnapGuide] = useState<{
    x: number | null;
    y: number | null;
  }>({ x: null, y: null });

  // Initialize layout state from slide's existing template or create default beautiful structure
  const [layout, setLayout] = useState(() => {
    if (slide.layoutTemplate) {
      return JSON.parse(JSON.stringify(slide.layoutTemplate));
    }

    // Try to find a matching blueprint for this slide index/concept from brandBlueprints
    let matchedBp = null;
    if (brandBlueprints && brandBlueprints.length > 0) {
      const slideConceptLower = (slide.concept || "").toLowerCase();
      const slideTextLower = (slide.overlayText || "").toLowerCase();
      
      // 1. Keyword search
      matchedBp = brandBlueprints.find(bp => {
        const bpName = (bp.name || "").toLowerCase();
        const bpCat = (bp.category || "").toLowerCase();
        return (bpName && (slideConceptLower.includes(bpName) || slideTextLower.includes(bpName))) || 
               (bpCat && (slideConceptLower.includes(bpCat) || slideTextLower.includes(bpCat)));
      });
      
      // 2. Fallback to first blueprint
      if (!matchedBp) {
        matchedBp = brandBlueprints[0];
      }
    }

    if (matchedBp && matchedBp.layoutTemplate) {
      const bpLt = JSON.parse(JSON.stringify(matchedBp.layoutTemplate));
      if (bpLt.boxes && bpLt.boxes.length > 0) {
        bpLt.boxes = parseOverlayTextToBoxes(slide.overlayText || "", bpLt.boxes);
      }
      
      return {
        ...bpLt,
        canvasEditingMode: "overlay",
        backgroundType: "image",
        backgroundImage: slide.backgroundSliceUrl || slide.url || slide.thumbnailUrl || ""
      };
    }

    // Default beautiful fallback layout
    const initialText = slide.overlayText || "";
    const headingText = initialText.split("\n")[0] || "Your Title Here";
    const bodyText = initialText.split("\n").slice(1).join("\n") || slide.concept || "Detailed information goes here.";
    const hasImage = !!(slide.backgroundSliceUrl || slide.url || slide.thumbnailUrl);
    const initialMode = "overlay";

    return {
      canvasEditingMode: initialMode,
      backgroundType: hasImage ? "image" : "solid",
      backgroundColor: "#FAF8F4",
      backgroundGradient: "linear-gradient(to bottom, #FAF8F4, #E2DCD5)",
      backgroundImage: slide.backgroundSliceUrl || slide.url || slide.thumbnailUrl || "",
      enableContentPlaque: slide.backgroundSliceUrl ? true : false,
      plaqueStyle: "elegant",
      plaqueBgColor: "#FFFFFF",
      plaqueOpacity: 0.95,
      plaqueBorderRadius: 16,
      plaquePadding: 20,
      plaqueTop: 15,
      plaqueLeft: 10,
      plaqueWidth: 80,
      plaqueHeight: 70,
      enableHeader: false,
      headerStyle: "simple",
      headerTextColor: "#333333",
      headerBgColor: "transparent",
      headerHeight: 10,
      headerShowLogo: true,
      headerShowEyebrow: false,
      headerFontSize: 12,
      enableFooter: true,
      footerStyle: "simple",
      footerText: brand?.companyName || "",
      footerTextColor: "#666666",
      footerBgColor: "transparent",
      footerHeight: 10,
      footerShowLogo: false,
      footerShowPageNum: true,
      footerShowUrl: false,
      footerUrlAlign: "center",
      footerTextAlign: "center",
      footerPageNumAlign: "right",
      logoPosition: "top-left",
      logoAlignment: "left",
      logoSize: 22,
      logoTop: 6,
      logoLeft: 6,
      boxes: [
        {
          id: "h1",
          type: "heading",
          text: headingText,
          top: 25,
          left: 15,
          width: 70,
          height: 18,
          fontSize: 22,
          color: "#1A1A1A",
          align: "center",
          fontWeight: "bold"
        },
        {
          id: "b1",
          type: "body",
          text: bodyText,
          top: 48,
          left: 15,
          width: 70,
          height: 25,
          fontSize: 13,
          color: "#4A4A4A",
          align: "center",
          fontWeight: "normal"
        }
      ]
    };
  });

  const updateLayout = (updates: any) => {
    setLayout((prev: any) => ({ ...prev, ...updates }));
  };

  const addTextBoxWithRole = (type: string) => {
    const id = "box_" + Math.random().toString(36).substr(2, 9);
    
    // Set default values based on role type
    let defaultText = "New text";
    let fontSize = 14;
    let fontWeight = "normal";
    let w = 60;
    let h = 15;
    
    if (type === "heading") {
      defaultText = "Your Best Smile Starts Here.";
      fontSize = 22;
      fontWeight = "bold";
      w = 80;
      h = 18;
    } else if (type === "subheading") {
      defaultText = "Welcome to Our Clinic";
      fontSize = 16;
      fontWeight = "medium";
      w = 50;
      h = 10;
    } else if (type === "eyebrow") {
      defaultText = "READY TO GET STARTED?";
      fontSize = 11;
      fontWeight = "bold";
      w = 30;
      h = 5;
    } else if (type === "body") {
      defaultText = "Enter your body paragraph text here. Keep it concise, engaging, and friendly to appeal to your audience.";
      fontSize = 14;
      w = 60;
      h = 20;
    } else if (type === "cta") {
      defaultText = "BOOK YOUR CONSULTATION →";
      fontSize = 12;
      fontWeight = "bold";
      w = 40;
      h = 8;
    } else if (type === "icon-list") {
      defaultText = "Premium Service\nExpert Care\nFriendly Environment";
      fontSize = 13;
      w = 50;
      h = 25;
    } else if (type === "logo") {
      defaultText = "";
      w = 20;
      h = 10;
    } else if (type === "disclaimer" || type === "footer") {
      defaultText = "*Terms and conditions apply.";
      fontSize = 8;
      w = 60;
      h = 5;
    } else if (type === "divider") {
      defaultText = "";
      fontSize = 14;
      w = 40;
      h = 2;
    }

    const newBox = {
      id,
      type,
      text: defaultText,
      top: 40,
      left: 20,
      width: w,
      height: h,
      fontSize,
      color: type === "divider" ? "#e5e7eb" : "#1A1A1A",
      align: "left",
      textAlign: "left",
      fontWeight,
      fontFamily: "sans",
      iconType: type === "icon-list" ? "check" : undefined,
      borderWidth: type === "divider" ? 4 : undefined
    };
    updateLayout({ boxes: [...(layout.boxes || []), newBox] });
    setSelectedBoxId(id);
  };

  const addTextBox = () => {
    addTextBoxWithRole("body");
  };

  const removeTextBox = (id: string) => {
    updateLayout({ boxes: (layout.boxes || []).filter((b: any) => b.id !== id) });
    if (selectedBoxId === id) setSelectedBoxId(null);
  };

  const updateBox = (id: string, updates: any) => {
    updateLayout({
      boxes: (layout.boxes || []).map((b: any) => b.id === id ? { ...b, ...updates } : b)
    });
  };

  const handlePointerMove = (e: MouseEvent | PointerEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    if (draggingLogo) {
      if (draggingLogo.type === "move") {
        let newLeft = ((e.clientX - rect.left - draggingLogo.offsetX) / rect.width) * 100;
        let newTop = ((e.clientY - rect.top - draggingLogo.offsetY) / rect.height) * 100;
        newLeft = Math.max(0, Math.min(newLeft, 100 - (layout.logoSize || 22)));
        newTop = Math.max(0, Math.min(newTop, 100 - (layout.logoSize || 22)));
        updateLayout({ logoLeft: Number(newLeft.toFixed(1)), logoTop: Number(newTop.toFixed(1)) });
      } else if (draggingLogo.type === "resize") {
        const currentLeftPx = ((layout.logoLeft || 5) / 100) * rect.width;
        let newSize = ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
        newSize = Math.max(5, Math.min(newSize, 100 - (layout.logoLeft || 5), 100 - (layout.logoTop || 5)));
        updateLayout({ logoSize: Number(newSize.toFixed(1)) });
      }
      return;
    }

    if (draggingPlaque) {
      if (draggingPlaque.type === "move") {
        let newLeft = ((e.clientX - rect.left - draggingPlaque.offsetX) / rect.width) * 100;
        let newTop = ((e.clientY - rect.top - draggingPlaque.offsetY) / rect.height) * 100;
        newLeft = Math.max(0, Math.min(newLeft, 100 - (layout.plaqueWidth ?? 80)));
        newTop = Math.max(0, Math.min(newTop, 100 - (layout.plaqueHeight ?? 70)));
        updateLayout({ plaqueLeft: Number(newLeft.toFixed(1)), plaqueTop: Number(newTop.toFixed(1)) });
      } else if (draggingPlaque.type === "resize") {
        const currentLeftPx = ((layout.plaqueLeft ?? 10) / 100) * rect.width;
        const currentTopPx = ((layout.plaqueTop ?? 15) / 100) * rect.height;
        let newWidth = ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
        let newHeight = ((e.clientY - rect.top - currentTopPx) / rect.height) * 100;
        newWidth = Math.max(10, Math.min(newWidth, 100 - (layout.plaqueLeft ?? 10)));
        newHeight = Math.max(10, Math.min(newHeight, 100 - (layout.plaqueTop ?? 15)));
        updateLayout({ plaqueWidth: Number(newWidth.toFixed(1)), plaqueHeight: Number(newHeight.toFixed(1)) });
      }
      return;
    }

    if (!dragging) return;
    const box = (layout.boxes || []).find((b: any) => b.id === dragging.id);
    if (!box) return;

    if (dragging.type === "move") {
      let newLeft = ((e.clientX - rect.left - dragging.offsetX) / rect.width) * 100;
      let newTop = ((e.clientY - rect.top - dragging.offsetY) / rect.height) * 100;

      const SNAP_THRESH = 2; // 2%
      let snapX = null;
      let snapY = null;

      (layout.boxes || []).forEach((other: any) => {
        if (other.id === box.id) return;
        // Snap left to other left
        if (Math.abs(newLeft - other.left) < SNAP_THRESH) {
          newLeft = other.left;
          snapX = other.left;
        }
        // Snap top to other top
        if (Math.abs(newTop - other.top) < SNAP_THRESH) {
          newTop = other.top;
          snapY = other.top;
        }
      });

      setSnapGuide({ x: snapX, y: snapY });

      newLeft = Math.max(0, Math.min(newLeft, 100 - box.width));
      newTop = Math.max(0, Math.min(newTop, 100 - box.height));
      updateBox(box.id, { left: Number(newLeft.toFixed(1)), top: Number(newTop.toFixed(1)) });
    } else {
      const currentLeftPx = (box.left / 100) * rect.width;
      const currentTopPx = (box.top / 100) * rect.height;
      let newWidth = ((e.clientX - rect.left - currentLeftPx) / rect.width) * 100;
      let newHeight = ((e.clientY - rect.top - currentTopPx) / rect.height) * 100;
      newWidth = Math.max(5, Math.min(newWidth, 100 - box.left));
      newHeight = Math.max(5, Math.min(newHeight, 100 - box.top));
      updateBox(box.id, { width: Number(newWidth.toFixed(1)), height: Number(newHeight.toFixed(1)) });
    }
   };

   const handlePointerUp = () => {
     setDragging(null);
     setDraggingLogo(null);
     setDraggingPlaque(null);
     setSnapGuide({ x: null, y: null });
   };

   useEffect(() => {
     const onMove = (e: PointerEvent) => {
       if (dragging || draggingLogo || draggingPlaque) {
         handlePointerMove(e);
       }
     };
     const onUp = () => {
       if (dragging || draggingLogo || draggingPlaque) {
         handlePointerUp();
       }
     };
     
     if (dragging || draggingLogo || draggingPlaque) {
       window.addEventListener("pointermove", onMove, { passive: true });
       window.addEventListener("pointerup", onUp);
     }
     
     return () => {
       window.removeEventListener("pointermove", onMove);
       window.removeEventListener("pointerup", onUp);
     };
   }, [dragging, draggingLogo, draggingPlaque, layout]);

  // Sync background image dynamically when generating new artwork via Gemini
  useEffect(() => {
    if (slide) {
      setLayout((prev: any) => {
        const currentBg = prev.backgroundImage;
        const newBg = slide.backgroundSliceUrl || slide.url || "";
        if (newBg && currentBg !== newBg) {
          return {
            ...prev,
            backgroundImage: newBg
          };
        }
        return prev;
      });
    }
  }, [slide.url, slide.backgroundSliceUrl]);

  const handleGenerateArtwork = async () => {
    setIsGenerating(true);
    try {
      await onRegenArtwork(layout);
    } finally {
      setIsGenerating(false);
    }
  };

  const activeBox = (layout.boxes || []).find((b: any) => b.id === selectedBoxId);

  const isOriginalMode = (layout.canvasEditingMode || "original") === "original";

  // Background style computation
  const bgStyle: React.CSSProperties = {};
  if (isOriginalMode) {
    const bgUrl = slide.url || slide.thumbnailUrl || slide.backgroundSliceUrl || "";
    if (bgUrl) {
      bgStyle.backgroundImage = `url(${bgUrl})`;
      bgStyle.backgroundSize = "cover";
      bgStyle.backgroundPosition = "center";
    } else {
      bgStyle.backgroundColor = "#FAF8F4";
    }
  } else {
    if (layout.backgroundType === "solid") {
      bgStyle.backgroundColor = layout.backgroundColor || "#FAF8F4";
    } else if (layout.backgroundType === "gradient") {
      bgStyle.backgroundImage = layout.backgroundGradient || "linear-gradient(to bottom, #FAF8F4, #E2DCD5)";
    } else if (layout.backgroundType === "image") {
      const bgUrl = layout.backgroundImage || slide.backgroundSliceUrl || slide.url || "";
      if (bgUrl) {
        bgStyle.backgroundImage = `url(${bgUrl})`;
        bgStyle.backgroundSize = "cover";
        bgStyle.backgroundPosition = "center";
      } else {
        bgStyle.backgroundColor = "#FAF8F4";
      }
    } else {
      bgStyle.backgroundColor = "#FAF8F4";
    }
  }

  // Pre-configured elegant gradients
  const gradientPresets = [
    { name: "Creamy Warmth", value: "linear-gradient(to bottom, #FAF8F4, #E9E2D8)" },
    { name: "Sage Organic", value: "linear-gradient(to bottom, #FAFDF6, #DBEAD5)" },
    { name: "Serene Slate", value: "linear-gradient(to bottom, #F7F9FC, #D4DFEE)" },
    { name: "Clean Pearl", value: "linear-gradient(to bottom, #FFFFFF, #EBEFF5)" },
    { name: "Mint Fresh", value: "linear-gradient(to bottom, #F0FBF7, #C6EFEB)" },
  ];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="bg-white rounded-3xl w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden shadow-2xl border border-gray-200 animate-in fade-in zoom-in-95 duration-200 text-gray-800">
        
        {/* Header bar */}
        <div className="px-8 py-4 border-b border-gray-150 flex items-center justify-between shrink-0 bg-white shadow-sm z-30">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-xl">
              <LayoutTemplate className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-950">Slide Layout & Content Editor</h3>
              <p className="text-xs text-gray-500 mt-0.5">Directly customize layout elements, values, color plates, and positioning coordinates.</p>
            </div>
          </div>
          
          {/* Custom Tab Selectors integrated neatly in header */}
          <div className="flex items-center gap-4">
            <div className="flex gap-1.5 p-1 bg-gray-100 rounded-xl border border-gray-200">
              <button
                onClick={() => setActiveTab("boxes")}
                className={`px-4 py-2 text-xs font-black rounded-lg transition-all flex items-center gap-1.5 ${activeTab === "boxes" ? "bg-white text-gray-950 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                <Type className="w-3.5 h-3.5" />
                <span>1. Text Payload</span>
              </button>
              <button
                onClick={() => setActiveTab("layout")}
                className={`px-4 py-2 text-xs font-black rounded-lg transition-all flex items-center gap-1.5 ${activeTab === "layout" ? "bg-white text-gray-950 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                <Palette className="w-3.5 h-3.5" />
                <span>2. Backdrop & Styling</span>
              </button>
            </div>

            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all border border-transparent hover:border-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden min-h-0 bg-gray-50">
          
          {/* Left Column: GENERATED CONTENT PAYLOAD */}
          <div className="w-[55%] border-r border-gray-150 flex flex-col overflow-hidden bg-gray-50">
            
            {/* Direct Blueprint Preset Apply */}
            {brandBlueprints.length > 0 && (
              <div className="p-4 bg-white border-b border-gray-100 shrink-0 flex items-center gap-3">
                <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider whitespace-nowrap block">
                  LAYOUT PRESETS
                </span>
                <select 
                  onChange={(e) => {
                    const bp = brandBlueprints.find(b => b.id === e.target.value);
                    if (bp?.layoutTemplate) {
                      const newLt = JSON.parse(JSON.stringify(bp.layoutTemplate));
                      
                      // 1. Keep active background image from current slide
                      const preservedBg = layout.backgroundImage || slide.backgroundSliceUrl || slide.url || slide.thumbnailUrl || "";
                      newLt.backgroundImage = preservedBg;
                      
                      // 2. Map current slide box texts into corresponding types of the preset to retain visual order & content roles
                      const currentHeading = (layout.boxes || []).find((b: any) => b.type === "heading" || b.id === "h1")?.text;
                      const currentEyebrow = (layout.boxes || []).find((b: any) => b.type === "eyebrow" || b.id?.includes("eyebrow"))?.text;
                      const currentBody = (layout.boxes || []).find((b: any) => b.type === "body" || b.id === "b1")?.text;
                      const currentCta = (layout.boxes || []).find((b: any) => b.type === "cta" || b.id?.includes("cta"))?.text;
                      const currentIconList = (layout.boxes || []).find((b: any) => b.type === "icon-list" || b.id === "bullets")?.text;
                      
                      if (newLt.boxes) {
                        newLt.boxes = newLt.boxes.map((b: any) => {
                          let t = b.text || "";
                          if ((b.type === "heading" || b.id === "h1") && currentHeading) t = currentHeading;
                          else if ((b.type === "eyebrow" || b.id?.includes("eyebrow")) && currentEyebrow) t = currentEyebrow;
                          else if ((b.type === "body" || b.id === "b1") && currentBody) t = currentBody;
                          else if ((b.type === "cta" || b.id?.includes("cta")) && currentCta) t = currentCta;
                          else if ((b.type === "icon-list" || b.id === "bullets") && currentIconList) t = currentIconList;
                          return { ...b, text: t };
                        });
                      }
                      
                      newLt.canvasEditingMode = "overlay";
                      setLayout(newLt);
                    }
                  }}
                  className="flex-1 text-xs p-2 rounded-xl border border-gray-200 bg-white text-gray-800 outline-none focus:ring-1 focus:ring-emerald-500 transition-all font-semibold cursor-pointer shadow-sm"
                >
                  <option value="">-- Apply a brand layout preset style --</option>
                  {brandBlueprints.map(bp => (
                    <option key={bp.id} value={bp.id}>{bp.name || `${bp.category} Template`}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Scrollable controls pane */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Tab: Bounding Boxes / Text Payload */}
              {activeTab === "boxes" && (
                <div className="space-y-6">
                  
                  {/* Title of active panel */}
                  <div className="flex items-center justify-between border-b border-gray-200 pb-2.5">
                    <h4 className="text-xs font-black uppercase text-gray-400 tracking-widest flex items-center gap-1.5">
                      <Type className="w-3.5 h-3.5 text-gray-400" />
                      GENERATED CONTENT PAYLOAD
                    </h4>

                    {/* Quick Add text box */}
                    <select 
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          addTextBoxWithRole(e.target.value);
                          e.target.value = "";
                        }
                      }}
                      className="text-[10px] uppercase font-bold p-1 bg-white border border-gray-200 text-gray-700 rounded-lg cursor-pointer outline-none shadow-sm hover:border-gray-300"
                    >
                      <option value="">+ Add Bounding Box...</option>
                      <option value="eyebrow">EYEBROW LABEL</option>
                      <option value="heading">HEADING TITLE</option>
                      <option value="subheading">SUBHEADING</option>
                      <option value="body">BODY PARAGRAPH</option>
                      <option value="cta">CTA TEXT OVERLAY</option>
                      <option value="disclaimer">DISCLAIMER / FOOTNOTE</option>
                      <option value="icon-list">ICON LIST (CHECKMARKS / BULLETS)</option>
                      <option value="divider">DIVIDER LINE</option>
                      <option value="logo">BRAND LOGO WATERMARK</option>
                    </select>
                  </div>

                  {/* List of active text box cards on light background */}
                  <div className="space-y-4">
                    {(layout.boxes || []).map((box: any, bIdx: number) => {
                      const isSelected = box.id === selectedBoxId;
                      return (
                        <div 
                          key={box.id}
                          onClick={() => setSelectedBoxId(box.id)}
                          className={`bg-white border text-left transition-all rounded-2xl p-5 shadow-sm hover:shadow relative cursor-pointer ${
                            isSelected 
                              ? "border-emerald-500 ring-1 ring-emerald-500/20" 
                              : "border-gray-250 hover:border-gray-300"
                          }`}
                        >
                          {/* Box title toolbar & delete */}
                          <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-[9px] font-black text-gray-600 uppercase tracking-widest">
                                {box.type || "text"}
                              </span>
                              <span className="text-[10px] text-gray-400 font-bold font-mono">
                                {box.id}
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTextBox(box.id);
                              }}
                              className="p-1 px-1.5 hover:text-red-500 text-gray-400 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete text box"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Live textarea editing block */}
                          <div className="space-y-1.5 mb-3.5" onClick={e => e.stopPropagation()}>
                            <label className="text-[9px] font-bold text-gray-450 uppercase tracking-widest block font-black">Content Payload Text</label>
                            {box.type === "icon-list" ? (
                              <ListItemsEditor
                                value={box.text}
                                onChange={(val) => updateBox(box.id, { text: val })}
                                defaultIconType={box.iconType}
                                defaultCustomIcon={box.customIcon}
                              />
                            ) : (
                              <textarea
                                value={box.text}
                                onChange={e => updateBox(box.id, { text: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-xs p-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 min-h-[60px] focus:bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all font-medium leading-relaxed"
                                placeholder="Type copywriting text overlay..."
                              />
                            )}
                          </div>

                          {/* Row 1 Properties (Color, Alignment, Font Size, Family Type) */}
                          <div className="grid grid-cols-4 gap-3 mb-3.5">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Color</label>
                              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl p-1 px-2 h-9">
                                <span className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block shrink-0" style={{ backgroundColor: box.color || "#1A1A1A" }} />
                                <input 
                                  type="text"
                                  value={box.color || ""}
                                  onChange={e => updateBox(box.id, { color: e.target.value })}
                                  onClick={e => e.stopPropagation()}
                                  className="w-full text-[10px] font-bold text-gray-900 bg-transparent outline-none p-0 border-none uppercase font-mono"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Align</label>
                              <select
                                value={box.align || "left"}
                                onChange={e => updateBox(box.id, { align: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Size (CQW)</label>
                              <input 
                                type="number"
                                min="0.5"
                                max="80.0"
                                step="0.1"
                                value={box.fontSize || 3.5}
                                onChange={e => updateBox(box.id, { fontSize: parseFloat(e.target.value) || 1.0 })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-xl px-2.5 h-9 outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Font family</label>
                              <select
                                value={box.fontFamily || "sans"}
                                onChange={e => updateBox(box.id, { fontFamily: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="sans">Sans-Serif</option>
                                <option value="serif">Serif Text</option>
                                <option value="mono">Monospace</option>
                                <option value="display">Tech / Outfit</option>
                              </select>
                            </div>
                          </div>

                          {/* Row 2 Properties (Percentage Position Coordinates) */}
                          <div className="grid grid-cols-4 gap-3 bg-gray-50 rounded-xl p-2.5 border border-gray-150">
                            <div>
                              <label className="text-[8px] font-black text-gray-450 uppercase block mb-0.5 text-center">Top (%)</label>
                              <input 
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={box.top ?? ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  updateBox(box.id, { top: val === "" ? "" : parseFloat(val) || 0 });
                                }}
                                onBlur={e => {
                                  const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                  updateBox(box.id, { top: Number(val.toFixed(1)) });
                                }}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-black text-gray-800 bg-white border border-gray-200 rounded-lg p-1 text-center outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="text-[8px] font-black text-gray-450 uppercase block mb-0.5 text-center">Left (%)</label>
                              <input 
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={box.left ?? ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  updateBox(box.id, { left: val === "" ? "" : parseFloat(val) || 0 });
                                }}
                                onBlur={e => {
                                  const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                  updateBox(box.id, { left: Number(val.toFixed(1)) });
                                }}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-black text-gray-800 bg-white border border-gray-200 rounded-lg p-1 text-center outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="text-[8px] font-black text-gray-450 uppercase block mb-0.5 text-center">Width (%)</label>
                              <input 
                                type="number"
                                min="5"
                                max="100"
                                step="0.1"
                                value={box.width ?? ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  updateBox(box.id, { width: val === "" ? "" : parseFloat(val) || 0 });
                                }}
                                onBlur={e => {
                                  const val = Math.max(5, Math.min(100, parseFloat(e.target.value) || 5));
                                  updateBox(box.id, { width: Number(val.toFixed(1)) });
                                }}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-black text-gray-800 bg-white border border-gray-200 rounded-lg p-1 text-center outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="text-[8px] font-black text-gray-450 uppercase block mb-0.5 text-center">Height (%)</label>
                              <input 
                                type="number"
                                min="5"
                                max="100"
                                step="0.1"
                                value={box.height ?? ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  updateBox(box.id, { height: val === "" ? "" : parseFloat(val) || 0 });
                                }}
                                onBlur={e => {
                                  const val = Math.max(5, Math.min(100, parseFloat(e.target.value) || 5));
                                  updateBox(box.id, { height: Number(val.toFixed(1)) });
                                }}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-black text-gray-800 bg-white border border-gray-200 rounded-lg p-1 text-center outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>
                          </div>

                          {/* Row 3 Properties (Advanced Font Options) */}
                          <div className="grid grid-cols-4 gap-3 mt-3">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Height Mode</label>
                              <select
                                value={box.flowNaturally ? "flow" : "fit"}
                                onChange={e => updateBox(box.id, { flowNaturally: e.target.value === "flow" })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="flow">Flow Text</option>
                                <option value="fit">Force Fit %</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Font Weight</label>
                              <select
                                value={box.fontWeight || "normal"}
                                onChange={e => updateBox(box.id, { fontWeight: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="normal">Normal</option>
                                <option value="medium">Medium</option>
                                <option value="bold">Bold</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Text Casing</label>
                              <select
                                value={box.textTransform || "none"}
                                onChange={e => updateBox(box.id, { textTransform: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="none">Normal</option>
                                <option value="uppercase">ALL CAPS</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Line Height</label>
                              <select
                                value={box.lineHeight || 1.1}
                                onChange={e => updateBox(box.id, { lineHeight: parseFloat(e.target.value) })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value={0.9}>Extra Tight</option>
                                <option value={1.0}>Tight</option>
                                <option value={1.1}>Normal</option>
                                <option value={1.2}>Relaxed</option>
                                <option value={1.4}>Loose</option>
                                <option value={1.6}>Double</option>
                              </select>
                            </div>
                          </div>

                          <div className="grid grid-cols-4 gap-3 mt-3">
                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Letter Gap</label>
                              <select
                                value={box.letterSpacing ?? "normal"}
                                onChange={e => updateBox(box.id, { letterSpacing: e.target.value })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              >
                                <option value="-0.04em">Tighter</option>
                                <option value="-0.02em">Tight</option>
                                <option value="normal">Normal</option>
                                <option value="0.05em">Wide</option>
                                <option value="0.1em">Widest</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Opacity (%)</label>
                              <input 
                                type="number"
                                min="0"
                                max="100"
                                value={box.opacity ?? 100}
                                onChange={e => updateBox(box.id, { opacity: parseInt(e.target.value) || 100 })}
                                onClick={e => e.stopPropagation()}
                                className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2.5 h-9 outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            </div>

                            {box.type === "icon-list" && (
                              <>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Icon Style</label>
                                  <select
                                    value={box.iconType || "check"}
                                    onChange={e => updateBox(box.id, { iconType: e.target.value })}
                                    onClick={e => e.stopPropagation()}
                                    className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2 h-9 outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                                  >
                                    <option value="check">Checkmark</option>
                                    <option value="cross">X Cross</option>
                                    <option value="dot">Bullet Dot</option>
                                    <option value="arrow">Arrow Right</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Icon Hue</label>
                                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl p-1 px-2 h-9">
                                    <span className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block shrink-0" style={{ backgroundColor: box.iconColor || box.color || "#1A1A1A" }} />
                                    <input 
                                      type="text"
                                      value={box.iconColor || ""}
                                      onChange={e => updateBox(box.id, { iconColor: e.target.value })}
                                      onClick={e => e.stopPropagation()}
                                      className="w-full text-[10px] font-bold text-gray-900 bg-transparent outline-none p-0 border-none uppercase font-mono"
                                      placeholder="Match"
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            {box.type === "divider" && (
                              <div>
                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Thickness (px)</label>
                                <input 
                                  type="number"
                                  min="1"
                                  max="40"
                                  value={box.borderWidth || 4}
                                  onChange={e => updateBox(box.id, { borderWidth: parseInt(e.target.value) || 4 })}
                                  onClick={e => e.stopPropagation()}
                                  className="w-full text-[11px] font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-2.5 h-9 outline-none focus:ring-1 focus:ring-emerald-500"
                                />
                              </div>
                            )}
                          </div>

                        </div>
                      );
                    })}

                    {(layout.boxes || []).length === 0 && (
                      <div className="p-8 text-center border border-dashed border-gray-200 bg-white rounded-2xl">
                        <Type className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-500 font-medium">No active bounding boxes. Click "Add Bounding Box" above to add overlay text.</p>
                      </div>
                    )}
                  </div>

                </div>
              )}

              {/* Tab: Backdrop, Branding, Logo & Plaque elements */}
              {activeTab === "layout" && (
                <div className="space-y-6">
                  
                  {/* Title of active panel */}
                  <div className="border-b border-gray-200 pb-2.5">
                    <h4 className="text-xs font-black uppercase text-gray-400 tracking-widest flex items-center gap-1.5">
                      <Palette className="w-3.5 h-3.5 text-gray-400" />
                      CANVAS BACKDROP & STYLING
                    </h4>
                  </div>

                  {/* Selector of BG type */}
                  <div className="space-y-2 bg-white border border-gray-150 p-5 rounded-2xl shadow-sm">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Backdrop Strategy</label>
                    <div className="grid grid-cols-3 gap-2">
                      {["solid", "gradient", "image"].map(bgType => (
                        <button
                          key={bgType}
                          onClick={() => updateLayout({ backgroundType: bgType })}
                          className={`py-2 text-[10px] font-black uppercase rounded-lg border text-center transition-all ${layout.backgroundType === bgType ? "bg-emerald-50 text-emerald-600 border-emerald-500 font-extrabold" : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"}`}
                        >
                          {bgType}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Solid Color picker */}
                  {layout.backgroundType === "solid" && (
                    <div className="space-y-4 bg-white border border-gray-150 p-5 rounded-2xl shadow-sm">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-405 uppercase tracking-wider block">Base Theme Color</label>
                        <div className="flex items-center gap-2">
                          <input 
                            type="text"
                            value={layout.backgroundColor || ""}
                            onChange={e => updateLayout({ backgroundColor: e.target.value })}
                            className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-mono"
                          />
                          <span className="w-8 h-8 rounded-lg border border-gray-200 shadow-sm" style={{ backgroundColor: layout.backgroundColor || "#FAF8F4" }} />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                        {["#FAF8F4", "#FFFFFF", "#F3F7F2", "#F0F5FA", "#1E293B", "#101B2E"].map(c => (
                          <button
                            key={c}
                            onClick={() => updateLayout({ backgroundColor: c })}
                            className="w-6 h-6 rounded-md border border-gray-300 flex items-center justify-center text-[8px]"
                            style={{ backgroundColor: c }}
                          >
                            {layout.backgroundColor === c && "✓"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gradient Presets picker */}
                  {layout.backgroundType === "gradient" && (
                    <div className="space-y-4 bg-white p-5 rounded-2xl border border-gray-150 shadow-sm">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Gradient Presets</label>
                      <div className="grid grid-cols-1 gap-2">
                        {gradientPresets.map((g, idx) => (
                          <button
                            key={idx}
                            onClick={() => updateLayout({ backgroundGradient: g.value })}
                            className={`p-2.5 rounded-xl border text-left transition-all font-semibold text-xs text-gray-900 shadow-sm bg-white hover:bg-gray-50 flex items-center gap-3 ${layout.backgroundGradient === g.value ? "border-emerald-500 ring-1 ring-emerald-500/10" : "border-gray-200"}`}
                          >
                            <span className="w-6 h-6 rounded-full inline-block shrink-0 shadow border border-white/5" style={{ backgroundImage: g.value }} />
                            <span>{g.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom Background Image URL */}
                  {layout.backgroundType === "image" && (
                    <div className="space-y-4 bg-white p-5 rounded-2xl border border-gray-150 shadow-sm">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Background Image URL</label>
                        <input 
                          type="text"
                          value={layout.backgroundImage || ""}
                          onChange={e => updateLayout({ backgroundImage: e.target.value })}
                          placeholder="Paste background image link..."
                          className="w-full p-2.5 text-xs bg-gray-50 border border-gray-200 text-gray-900 rounded-xl placeholder:text-gray-400 outline-none focus:bg-white focus:ring-1 focus:ring-emerald-500 font-medium"
                        />
                      </div>
                      
                      <div className="space-y-1.5 pt-2 border-t border-gray-100">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                          <span>AI Backdrop Guidance Prompt</span>
                          <span className="bg-emerald-100 text-emerald-700 text-[8px] scale-90 px-1 py-0.5 rounded uppercase font-black tracking-widest">Gemini</span>
                        </label>
                        <textarea
                          value={layout.bgGenPrompt || ""}
                          onChange={e => updateLayout({ bgGenPrompt: e.target.value })}
                          placeholder="Example: A luxurious dental cabinet interior with warm lighting, wood tones, clean modern dentist office, soft atmospheric blur..."
                          rows={3}
                          className="w-full p-2.5 text-xs bg-gray-50 border border-gray-200 text-gray-900 rounded-xl placeholder:text-gray-400 outline-none focus:bg-white focus:ring-1 focus:ring-emerald-500 text-left whitespace-pre-wrap leading-relaxed font-medium placeholder:text-gray-400"
                        />
                        <p className="text-[9px] text-gray-400 leading-normal">
                          This instruction tells the Gemini image generator precisely how to blend and compose the scenic textless background. Useful for maintaining a strict visual aesthetic.
                        </p>
                      </div>

                      {(slide.backgroundSliceUrl || slide.url) && (
                        <button
                          type="button"
                          onClick={() => updateLayout({ backgroundImage: slide.backgroundSliceUrl || slide.url || "" })}
                          className="w-full py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-xl transition-all cursor-pointer shadow-sm border border-emerald-100"
                        >
                          Reset to AI Generated Backdrop Image
                        </button>
                      )}
                    </div>
                  )}

                  {/* Content Plaque settings */}
                  <div className="space-y-4 bg-white border border-gray-150 p-5 rounded-2xl shadow-sm">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-emerald-600" />
                        <label className="text-xs font-black text-gray-750">Enable Plaque Plate Box</label>
                      </div>
                      <input 
                        type="checkbox"
                        checked={layout.enableContentPlaque || false}
                        onChange={e => updateLayout({ enableContentPlaque: e.target.checked })}
                        className="w-4 h-4 text-emerald-600 border-gray-350 outline-none rounded cursor-pointer accent-emerald-500"
                      />
                    </div>

                    {layout.enableContentPlaque && (
                      <div className="space-y-4 pt-2">
                        
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                            Plaque Layout Style
                          </label>
                          <select
                            className="w-full text-sm border border-gray-200 rounded-lg p-2 bg-white text-gray-950 font-semibold cursor-pointer"
                            value={layout.plaqueStyle || "solid"}
                            onChange={(e) => {
                              const style = e.target.value;
                              let updates: any = { plaqueStyle: style };
                              if (style === "split-left") {
                                updates.plaqueLeft = 0;
                                updates.plaqueTop = 0;
                                updates.plaqueWidth = 55;
                                updates.plaqueHeight = 100;
                                updates.plaqueBorderRadius = 0;
                                updates.plaqueBgColor = "rgba(255, 255, 255, 0.95)";
                                updates.plaqueOpacity = 100;
                              } else if (style === "split-right") {
                                updates.plaqueLeft = 45;
                                updates.plaqueTop = 0;
                                updates.plaqueWidth = 55;
                                updates.plaqueHeight = 100;
                                updates.plaqueBorderRadius = 0;
                                updates.plaqueBgColor = "rgba(255, 255, 255, 0.95)";
                                updates.plaqueOpacity = 100;
                              } else if (style === "educational-tip") {
                                updates.plaqueLeft = 0;
                                updates.plaqueTop = 0;
                                updates.plaqueWidth = 55;
                                updates.plaqueHeight = 100;
                                updates.plaqueBorderRadius = 0;
                                updates.plaqueBgColor = "#778a78";
                                updates.plaqueOpacity = 100;
                              } else if (style === "glass") {
                                updates.plaqueLeft = 15;
                                updates.plaqueTop = 15;
                                updates.plaqueWidth = 70;
                                updates.plaqueHeight = 70;
                                updates.plaqueBorderRadius = 16;
                                updates.plaqueBgColor = "rgba(255, 255, 255, 0.25)";
                                updates.plaqueOpacity = 100;
                              } else if (style === "floating-modern") {
                                updates.plaqueLeft = 15;
                                updates.plaqueTop = 15;
                                updates.plaqueWidth = 70;
                                updates.plaqueHeight = 70;
                                updates.plaqueBorderRadius = 24;
                                updates.plaqueBgColor = "rgba(255, 255, 255, 0.95)";
                                updates.plaqueOpacity = 100;
                              } else if (style === "bottom-story") {
                                updates.plaqueLeft = 0;
                                updates.plaqueTop = 60;
                                updates.plaqueWidth = 100;
                                updates.plaqueHeight = 40;
                                updates.plaqueBorderRadius = 0;
                                updates.plaqueBgColor = "rgba(255, 255, 255, 0.95)";
                                updates.plaqueOpacity = 100;
                              }
                              updateLayout(updates);
                            }}
                          >
                            <option value="solid">Solid Card</option>
                            <option value="solid-shadow">Floating Card (Shadow)</option>
                            <option value="glass">Glassmorphism</option>
                            <option value="split-left">Split Left (Full Height)</option>
                            <option value="split-right">Split Right (Full Height)</option>
                            <option value="educational-tip">Corner Tip Box</option>
                            <option value="magazine-diagonal">Diagonal Edge</option>
                            <option value="floating-modern">Modern Floating</option>
                            <option value="bottom-story">Bottom Third Overlay</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 gap-3.5">
                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-450 uppercase font-bold">Rounded Borders ({layout.plaqueBorderRadius}px)</label>
                            <input 
                              type="range"
                              min="0"
                              max="32"
                              value={layout.plaqueBorderRadius || 0}
                              onChange={e => updateLayout({ plaqueBorderRadius: parseInt(e.target.value) })}
                              className="w-full bg-gray-200 rounded h-1 accent-emerald-500"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-455 uppercase font-bold">Opacity ({Math.round(layout.plaqueOpacity ?? 100)}%)</label>
                            <input 
                              type="range"
                              min="10"
                              max="100"
                              value={Math.round(layout.plaqueOpacity ?? 100)}
                              onChange={e => updateLayout({ plaqueOpacity: parseInt(e.target.value) })}
                              className="w-full bg-gray-200 rounded h-1 accent-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Coordinates of the plaque container */}
                        <div className="grid grid-cols-4 gap-2 bg-gray-50 border border-gray-150 p-2.5 rounded-xl text-center">
                          <div>
                            <span className="text-[8px] font-black text-gray-400 block mb-0.5">Top (%)</span>
                            <input 
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={layout.plaqueTop ?? ""}
                              onChange={e => {
                                const val = e.target.value;
                                updateLayout({ plaqueTop: val === "" ? "" : parseFloat(val) || 0 });
                              }}
                              onBlur={e => {
                                const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                updateLayout({ plaqueTop: Number(val.toFixed(1)) });
                              }}
                              className="w-full text-center text-[11px] font-bold text-gray-900 bg-white border border-gray-200 rounded-md p-1"
                            />
                          </div>
                          <div>
                            <span className="text-[8px] font-black text-gray-400 block mb-0.5">Left (%)</span>
                            <input 
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={layout.plaqueLeft ?? ""}
                              onChange={e => {
                                const val = e.target.value;
                                updateLayout({ plaqueLeft: val === "" ? "" : parseFloat(val) || 0 });
                              }}
                              onBlur={e => {
                                const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                updateLayout({ plaqueLeft: Number(val.toFixed(1)) });
                              }}
                              className="w-full text-center text-[11px] font-bold text-gray-900 bg-white border border-gray-200 rounded-md p-1"
                            />
                          </div>
                          <div>
                            <span className="text-[8px] font-black text-gray-400 block mb-0.5">Width (%)</span>
                            <input 
                              type="number"
                              min="10"
                              max="100"
                              step="0.1"
                              value={layout.plaqueWidth ?? ""}
                              onChange={e => {
                                const val = e.target.value;
                                updateLayout({ plaqueWidth: val === "" ? "" : parseFloat(val) || 0 });
                              }}
                              onBlur={e => {
                                const val = Math.max(10, Math.min(100, parseFloat(e.target.value) || 10));
                                updateLayout({ plaqueWidth: Number(val.toFixed(1)) });
                              }}
                              className="w-full text-center text-[11px] font-bold text-gray-900 bg-white border border-gray-200 rounded-md p-1"
                            />
                          </div>
                          <div>
                            <span className="text-[8px] font-black text-gray-400 block mb-0.5">Height (%)</span>
                            <input 
                              type="number"
                              min="10"
                              max="100"
                              step="0.1"
                              value={layout.plaqueHeight ?? ""}
                              onChange={e => {
                                const val = e.target.value;
                                updateLayout({ plaqueHeight: val === "" ? "" : parseFloat(val) || 0 });
                              }}
                              onBlur={e => {
                                const val = Math.max(10, Math.min(100, parseFloat(e.target.value) || 10));
                                updateLayout({ plaqueHeight: Number(val.toFixed(1)) });
                              }}
                              className="w-full text-center text-[11px] font-bold text-gray-900 bg-white border border-gray-200 rounded-md p-1"
                            />
                          </div>
                        </div>

                        {/* Plaque Background Fill */}
                        <div className="grid grid-cols-2 gap-4 items-center">
                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-400 uppercase font-black">Fill Color</label>
                            <input 
                              type="text"
                              value={layout.plaqueBgColor || ""}
                              onChange={e => updateLayout({ plaqueBgColor: e.target.value })}
                              className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-900 font-mono"
                            />
                          </div>
                          <div className="flex gap-1.5 pt-2 flex-wrap">
                            {["#FFFFFF", "#FAF8F4", "#0C1322", "#18233C"].map(pc => (
                              <button
                                key={pc}
                                onClick={() => updateLayout({ plaqueBgColor: pc })}
                                className="w-5 h-5 rounded border border-gray-300 shadow-sm shrink-0"
                                style={{ backgroundColor: pc }}
                              />
                            ))}
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                  
                   {/* Brand Logo Settings */}
                   {brand?.logoUrl && (
                     <div className="space-y-4 bg-white border border-gray-150 p-5 rounded-2xl shadow-sm">
                       <span className="text-[10px] font-black text-gray-400 uppercase block border-b border-gray-100 pb-1.5">Brand Logo Overlay</span>
                       
                       <div className="space-y-1">
                         <label className="text-[9px] text-gray-450 uppercase font-black block mb-1">Overlay Logo Position</label>
                         <select
                           className="w-full text-xs font-semibold border border-gray-200 rounded-lg p-2 bg-white text-gray-950 cursor-pointer text-gray-900"
                           value={layout.logoPosition || "floating"}
                           onChange={(e) => updateLayout({ logoPosition: e.target.value })}
                         >
                           <option value="none">Disabled / Handled by header/footer</option>
                           <option value="content-top">Standard Top Overlay (Y: 5%)</option>
                           <option value="content-bottom">Standard Bottom Overlay (Y: 85%)</option>
                           <option value="floating">Fully Customizable Floating Logo</option>
                         </select>
                       </div>

                       {(layout.logoPosition || "floating") !== "none" && (
                         <>
                           <div className="grid grid-cols-2 gap-4">
                             <div className="space-y-1">
                               <label className="text-[9px] text-gray-450 uppercase font-bold">Logo Size ({layout.logoSize || 15}%)</label>
                               <input 
                                 type="range"
                                 min="5"
                                 max="40"
                                 value={layout.logoSize || 15}
                                 onChange={e => updateLayout({ logoSize: parseInt(e.target.value) })}
                                 className="w-full bg-gray-200 rounded h-1 accent-emerald-500"
                                />
                             </div>

                             {(layout.logoPosition || "floating") !== "floating" && (
                               <div className="space-y-1">
                                 <label className="text-[9px] text-gray-455 uppercase font-bold">Alignment</label>
                                 <div className="flex gap-1">
                                   {["left", "center", "right"].map(align => (
                                     <button
                                       key={align}
                                       type="button"
                                       onClick={() => updateLayout({ logoAlignment: align })}
                                       className={`flex-1 py-1.5 text-[9px] font-bold uppercase rounded-lg border text-center transition-all ${layout.logoAlignment === align ? "bg-emerald-50 text-emerald-600 border-emerald-500" : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"}`}
                                     >
                                       {align}
                                     </button>
                                   ))}
                                 </div>
                               </div>
                             )}
                           </div>

                           {(layout.logoPosition || "floating") === "floating" && (
                             <div className="grid grid-cols-2 gap-4 pt-1 flex-wrap">
                               <div className="space-y-1">
                                 <span className="text-[8px] font-black text-gray-400 block mb-0.5">Logo Top (%)</span>
                                 <input 
                                   type="number"
                                   min="0"
                                   max="100"
                                   step="0.1"
                                   value={layout.logoTop ?? 5}
                                   onChange={e => {
                                     const val = e.target.value;
                                     updateLayout({ logoTop: val === "" ? "" : parseFloat(val) || 0 });
                                   }}
                                   onBlur={e => {
                                     const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                     updateLayout({ logoTop: Number(val.toFixed(1)) });
                                   }}
                                   className="w-full text-[11px] font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-center outline-none"
                                 />
                               </div>
                               <div className="space-y-1">
                                 <span className="text-[8px] font-black text-gray-400 block mb-0.5">Logo Left (%)</span>
                                 <input 
                                   type="number"
                                   min="0"
                                   max="100"
                                   step="0.1"
                                   value={layout.logoLeft ?? 5}
                                   onChange={e => {
                                     const val = e.target.value;
                                     updateLayout({ logoLeft: val === "" ? "" : parseFloat(val) || 0 });
                                   }}
                                   onBlur={e => {
                                     const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                     updateLayout({ logoLeft: Number(val.toFixed(1)) });
                                   }}
                                   className="w-full text-[11px] font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-center outline-none"
                                 />
                               </div>
                             </div>
                           )}
                         </>
                       )}
                     </div>
                   )}

                  {/* Header / Footer options */}
                  <div className="grid grid-cols-2 gap-4">
                    
                    {/* Header Options */}
                    <div className="space-y-4 bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-black text-gray-700">Enable Header / Logo Bar</label>
                        <input 
                          type="checkbox"
                          checked={layout.enableHeader || false}
                          onChange={e => updateLayout({ enableHeader: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500 rounded cursor-pointer text-emerald-600"
                        />
                      </div>
                      {layout.enableHeader && (
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <label className="text-[9px] text-gray-400 uppercase font-black block">Text Color</label>
                              <input 
                                type="text"
                                value={layout.headerTextColor || "#333333"}
                                onChange={e => updateLayout({ headerTextColor: e.target.value })}
                                className="w-full p-2 bg-gray-50 border border-gray-200 text-xs text-gray-900 rounded-lg font-mono"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[9px] text-gray-400 uppercase font-black block">Background</label>
                              <input 
                                type="text"
                                value={layout.headerBgColor || "transparent"}
                                onChange={e => updateLayout({ headerBgColor: e.target.value })}
                                className="w-full p-2 bg-gray-50 border border-gray-200 text-xs text-gray-900 rounded-lg font-mono"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-450 uppercase font-bold">Header Height ({layout.headerHeight || 10}%)</label>
                            <input 
                              type="range"
                              min="5"
                              max="30"
                              value={layout.headerHeight || 10}
                              onChange={e => updateLayout({ headerHeight: parseInt(e.target.value) })}
                              className="w-full bg-gray-200 rounded h-1 accent-emerald-500"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-400 uppercase font-bold">Header Bar Style</label>
                            <select
                              className="w-full text-xs border border-gray-200 rounded-lg p-2 bg-white text-gray-950"
                              value={layout.headerStyle || "simple"}
                              onChange={e => updateLayout({ headerStyle: e.target.value })}
                            >
                              <option value="simple">Simple Transparent</option>
                              <option value="bordered">Bottom Border</option>
                              <option value="accent-bar">Brand Accent Bar</option>
                              <option value="glass">Glassmorphism</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Options */}
                    <div className="space-y-4 bg-white p-5 rounded-2xl border border-gray-150 shadow-sm flex-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-black text-gray-700">Enable Structural Footer</label>
                        <input 
                          type="checkbox"
                          checked={layout.enableFooter || false}
                          onChange={e => updateLayout({ enableFooter: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500 rounded cursor-pointer text-emerald-600"
                        />
                      </div>
                      {layout.enableFooter && (
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-400 uppercase font-black block">Footer Label Text</label>
                            <input 
                              type="text"
                              value={layout.footerText || ""}
                              onChange={e => updateLayout({ footerText: e.target.value })}
                              className="w-full p-2 bg-gray-50 border border-gray-200 text-xs text-gray-900 rounded-lg"
                              placeholder="e.g. [@company]"
                            />
                          </div>
                      
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <label className="text-[9px] text-gray-400 uppercase font-black block">Text Color</label>
                              <input 
                                type="text"
                                value={layout.footerTextColor || "#666666"}
                                onChange={e => updateLayout({ footerTextColor: e.target.value })}
                                className="w-full p-2 bg-gray-50 border border-gray-200 text-xs text-gray-900 rounded-lg font-mono"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[9px] text-gray-400 uppercase font-black block">Background Color</label>
                              <input 
                                type="text"
                                value={layout.footerBgColor || "transparent"}
                                onChange={e => updateLayout({ footerBgColor: e.target.value })}
                                className="w-full p-2 bg-gray-50 border border-gray-200 text-xs text-gray-900 rounded-lg font-mono"
                              />
                            </div>
                          </div>

                           <div className="space-y-1">
                            <label className="text-[9px] text-gray-450 uppercase font-bold">Footer Height ({layout.footerHeight || 10}%)</label>
                            <input 
                              type="range"
                              min="5"
                              max="30"
                              value={layout.footerHeight || 10}
                              onChange={e => updateLayout({ footerHeight: parseInt(e.target.value) })}
                              className="w-full bg-gray-200 rounded h-1 accent-emerald-500"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-400 uppercase font-bold">Footer Bar Style</label>
                            <select
                              className="w-full text-xs font-semibold border border-gray-200 rounded-lg p-2 bg-white text-gray-950 cursor-pointer text-gray-900"
                              value={layout.footerStyle || "simple"}
                              onChange={e => updateLayout({ footerStyle: e.target.value })}
                            >
                              <option value="simple">Simple Transparent</option>
                              <option value="bordered">Top Border</option>
                              <option value="accent-bar">Brand Accent Bar</option>
                              <option value="glass">Glassmorphism</option>
                            </select>
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] text-gray-400 uppercase font-bold">Footer Alignments</label>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-[8px] text-gray-450 uppercase font-black block mb-0.5">Text Align</span>
                                <select
                                  className="w-full text-[10px] font-bold border border-gray-200 rounded p-1 bg-white cursor-pointer text-gray-900"
                                  value={layout.footerTextAlign || "left"}
                                  onChange={e => updateLayout({ footerTextAlign: e.target.value })}
                                >
                                  <option value="left">Left</option>
                                  <option value="right">Right</option>
                                  <option value="center">Center</option>
                                </select>
                              </div>
                              <div>
                                <span className="text-[8px] text-gray-450 uppercase font-black block mb-0.5">Url Align</span>
                                <select
                                  className="w-full text-[10px] font-bold border border-gray-200 rounded p-1 bg-white cursor-pointer text-gray-900"
                                  value={layout.footerUrlAlign || "right"}
                                  onChange={e => updateLayout({ footerUrlAlign: e.target.value })}
                                >
                                  <option value="left">Left</option>
                                  <option value="right">Right</option>
                                  <option value="none">None</option>
                                </select>
                              </div>
                            </div>
                          </div>

                          <div className="pt-2 grid grid-cols-2 gap-2 border-t border-gray-100">
                            <label className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-500 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={layout.footerShowPageNum !== false}
                                onChange={e => updateLayout({ footerShowPageNum: e.target.checked })}
                                className="rounded text-emerald-600 border-gray-300 w-3.5 h-3.5 accent-emerald-500 cursor-pointer"
                              />
                              <span>Show Page Number</span>
                            </label>
                            <label className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-500 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={layout.footerShowUrl !== false}
                                onChange={e => updateLayout({ footerShowUrl: e.target.checked })}
                                className="rounded text-emerald-600 border-gray-300 w-3.5 h-3.5 accent-emerald-500 cursor-pointer"
                              />
                              <span>Show Company URL</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                </div>
              )}

            </div>
          </div>

          {/* Right Column: RENDERED OUTPUT */}
          <div className="flex-1 p-8 flex flex-col items-center justify-between bg-zinc-50 overflow-y-auto">
            
            {/* Header block with download button matching screenshot */}
            <div className="w-full max-w-[400px] flex items-center justify-between border-b border-gray-200 pb-3 shrink-0 mb-4 px-1">
              <div>
                <h4 className="text-xs font-black uppercase text-gray-400 tracking-widest block leading-none">
                  RENDERED OUTPUT
                </h4>
                <p className="text-[10px] text-gray-500 mt-1">Live responsive canvas mockup</p>
              </div>

              {/* Direct image baking download */}
              <button
                onClick={() => {
                  if (isOriginalMode) {
                    const dlUrl = slide.url || slide.thumbnailUrl || slide.backgroundSliceUrl || "";
                    if (dlUrl) {
                      const link = document.createElement("a");
                      link.download = `slide-export-${slide.id || "render"}.png`;
                      link.href = dlUrl;
                      link.target = "_blank";
                      link.click();
                      onSave(layout);
                    }
                    return;
                  }
                  onSave(layout).then(() => {
                    const bakeContainer = document.createElement("div");
                    bakeContainer.style.position = "absolute";
                    bakeContainer.style.left = "-9999px";
                    bakeContainer.style.top = "-9999px";
                    bakeContainer.style.width = "1080px";
                    bakeContainer.style.height = is916 ? "1920px" : "1080px";
                    bakeContainer.style.containerType = "inline-size";
                    bakeContainer.style.overflow = "hidden";
                    
                    const bgUrl = layout.backgroundImage || slide.backgroundSliceUrl || slide.url || "";
                    if (layout.backgroundType === "solid") {
                      bakeContainer.style.backgroundColor = layout.backgroundColor || "#FAF8F4";
                    } else if (layout.backgroundType === "gradient") {
                      bakeContainer.style.backgroundImage = layout.backgroundGradient || "linear-gradient(to bottom, #FAF8F4, #E2DCD5)";
                    } else if (layout.backgroundType === "image" && bgUrl) {
                      const bgImg = document.createElement("img");
                      bgImg.src = bgUrl;
                      bgImg.crossOrigin = "anonymous";
                      bgImg.referrerPolicy = "no-referrer";
                      bgImg.style.position = "absolute";
                      bgImg.style.inset = "0";
                      bgImg.style.width = "100%";
                      bgImg.style.height = "100%";
                      bgImg.style.objectFit = "cover";
                      bakeContainer.appendChild(bgImg);
                    }

                    if (layout.enableContentPlaque) {
                      const plaque = document.createElement("div");
                      plaque.style.position = "absolute";
                      plaque.style.top = `${layout.plaqueTop ?? 15}%`;
                      plaque.style.left = `${layout.plaqueLeft ?? 10}%`;
                      plaque.style.width = `${layout.plaqueWidth ?? 80}%`;
                      plaque.style.height = `${layout.plaqueHeight ?? 70}%`;
                      plaque.style.backgroundColor = layout.plaqueBgColor || layout.plaqueColor || "#FFFFFF";
                      plaque.style.opacity = String((layout.plaqueOpacity ?? 100) / 100);
                      plaque.style.borderRadius = `${(layout.plaqueBorderRadius ?? 16) * 4}px`;
                      
                      const style = layout.plaqueStyle || "solid";
                      if (style === "glass" || style === "floating-modern") {
                        plaque.style.backdropFilter = "blur(64px)";
                      }
                      if (style === "glass" || style === "floating-modern" || style === "split-left" || style === "split-right") {
                        plaque.style.boxShadow = "0 30px 90px rgba(0,0,0,0.08)";
                      } else if (style === "solid-shadow") {
                        plaque.style.boxShadow = "0 60px 120px rgba(0,0,0,0.12)";
                      } else {
                        plaque.style.boxShadow = "none";
                      }
                      if (style === "glass") {
                        plaque.style.border = "2px solid rgba(255, 255, 255, 0.4)";
                      } else {
                        plaque.style.border = "1px solid rgba(0,0,0,0.05)";
                      }
                      plaque.style.zIndex = "1";
                      bakeContainer.appendChild(plaque);
                    }

                    if (layout.logoPosition !== "none" && layout.logoPosition !== "header" && layout.logoPosition !== "footer" && brand?.logoUrl) {
                      const logoWrap = document.createElement("div");
                      logoWrap.style.position = "absolute";
                      
                      const leftVal = layout.logoPosition === "floating"
                        ? `${layout.logoLeft || 5}%`
                        : (layout.logoAlignment || "left") === "left"
                          ? "5%"
                          : layout.logoAlignment === "center"
                            ? "42.5%"
                            : "80%";
                      const topVal = layout.logoPosition === "floating"
                        ? `${layout.logoTop || 5}%`
                        : (layout.logoPosition || "content-top") === "content-top"
                          ? "5%"
                          : "85%";
                      const sizeVal = `${layout.logoSize || 15}%`;

                      logoWrap.style.left = leftVal;
                      logoWrap.style.top = topVal;
                      logoWrap.style.width = sizeVal;
                      logoWrap.style.height = sizeVal;
                      logoWrap.style.display = "flex";
                      logoWrap.style.justifyContent = (layout.logoAlignment || "left") === "center" ? "center" : (layout.logoAlignment || "left") === "right" ? "flex-end" : "flex-start";
                      logoWrap.style.alignItems = "center";
                      logoWrap.style.zIndex = "10";
                      
                      const logoImg = document.createElement("img");
                      logoImg.src = brand.logoUrl;
                      logoImg.crossOrigin = "anonymous";
                      logoImg.referrerPolicy = "no-referrer";
                      logoImg.style.maxWidth = "100%";
                      logoImg.style.maxHeight = "100%";
                      logoImg.style.objectFit = "contain";
                      logoWrap.appendChild(logoImg);
                      bakeContainer.appendChild(logoWrap);
                    }

                    if (layout.enableHeader) {
                      const header = document.createElement("div");
                      header.style.position = "absolute";
                      header.style.top = "0";
                      header.style.left = "0";
                      header.style.right = "0";
                      header.style.display = "flex";
                      header.style.alignItems = "center";
                      header.style.justifyContent = "space-between";
                      header.style.padding = "0 40px";
                      header.style.height = `${layout.headerHeight || 10}%`;
                      header.style.backgroundColor = layout.headerBgColor || "transparent";
                      header.style.color = layout.headerTextColor || "#333333";
                      header.style.borderBottom = layout.headerStyle === "bordered" ? "1px solid rgba(0,0,0,0.08)" : layout.headerStyle === "accent-bar" ? `2px solid ${layout.headerTextColor || "#333"}` : "none";
                      header.style.zIndex = "10";
                      
                      const rightWrap = document.createElement("div");
                      if (layout.headerShowLogo && brand?.logoUrl) {
                        const logoImg = document.createElement("img");
                        logoImg.src = brand.logoUrl;
                        logoImg.crossOrigin = "anonymous";
                        logoImg.referrerPolicy = "no-referrer";
                        logoImg.style.height = "40px";
                        logoImg.style.width = "auto";
                        logoImg.style.objectFit = "contain";
                        rightWrap.appendChild(logoImg);
                      }
                      header.appendChild(document.createElement("div")); // empty left
                      header.appendChild(rightWrap);
                      bakeContainer.appendChild(header);
                    }

                    (layout.boxes || []).forEach((b: any) => {
                      const textBlock = document.createElement("div");
                      textBlock.style.position = "absolute";
                      textBlock.style.top = `${b.top}%`;
                      textBlock.style.left = `${b.left}%`;
                      textBlock.style.width = `${b.width}%`;
                      textBlock.style.height = b.flowNaturally ? "auto" : `${b.height}%`;
                      if (b.flowNaturally) textBlock.style.minHeight = `${b.height}%`;
                      textBlock.style.textAlign = b.textAlign || b.align || "left";
                      textBlock.style.color = b.color || "#1A1A1A";
                      const rawFontSize = b.fontSize && b.fontSize < 12 
                        ? b.fontSize * 10.8 
                        : (b.fontSize || 16) * 2.16;
                      textBlock.style.fontSize = `${rawFontSize}px`;
                      textBlock.style.fontWeight = b.fontWeight === "bold" ? "700" : b.fontWeight === "medium" ? "500" : "400";
                      textBlock.style.display = "flex";
                      textBlock.style.flexDirection = b.type === "icon-list" ? "column" : "column";
                      if (b.type === "icon-list") {
                         textBlock.style.gap = `${rawFontSize * 0.4}px`;
                      } else {
                         textBlock.style.justifyContent = b.type === "heading" ? "flex-end" : "center";
                      }
                      textBlock.style.alignItems = (b.textAlign || b.align) === "center" ? "center" : (b.textAlign || b.align) === "right" ? "flex-end" : "flex-start";
                      textBlock.style.lineHeight = String(b.lineHeight ?? 1.1);
                      textBlock.style.letterSpacing = b.letterSpacing ?? "-0.02em";
                      textBlock.style.transform = `translate(${b.xOffset || 0}em, ${b.yOffset || 0}em)`;
                      textBlock.style.textTransform = b.textTransform === "uppercase" ? "uppercase" : "none";
                      textBlock.style.zIndex = "20";
                      textBlock.style.fontFamily = b.fontFamily === "serif" ? "Georgia, serif" : b.fontFamily === "mono" ? "monospace" : b.fontFamily === "display" ? "'Space Grotesk', sans-serif" : "Inter, system-ui, sans-serif";
                      
                      if (b.type === "logo") {
                        if (brand?.logoUrl) {
                          const img = document.createElement("img");
                          img.src = brand.logoUrl;
                          img.crossOrigin = "anonymous";
                          img.referrerPolicy = "no-referrer";
                          img.style.maxWidth = "100%";
                          img.style.maxHeight = "100%";
                          img.style.objectFit = "contain";
                          img.style.mixBlendMode = "multiply";
                          textBlock.appendChild(img);
                        }
                      } else if (b.type === "icon-list") {
                        const lines = (b.text || "").split("\n").filter(Boolean);
                        lines.forEach((line: string) => {
                          const lineDiv = document.createElement("div");
                          lineDiv.style.display = "flex";
                          lineDiv.style.flexDirection = "row";
                          lineDiv.style.alignItems = "flex-start";
                          lineDiv.style.gap = `${rawFontSize * 0.5}px`;
                          lineDiv.style.width = "100%";
                          lineDiv.style.justifyContent = (b.textAlign || b.align) === "center" ? "center" : (b.textAlign || b.align) === "right" ? "flex-end" : "flex-start";
                          
                          const iconDiv = document.createElement("div");
                          iconDiv.style.color = b.iconColor || b.color || "#333";
                          iconDiv.style.display = "flex";
                          iconDiv.style.alignItems = "center";
                          iconDiv.style.justifyContent = "center";
                          
                          // Simplified dot icon for native DOM rendering match
                          const dot = document.createElement("div");
                          dot.style.width = `${rawFontSize * 0.4}px`;
                          dot.style.height = `${rawFontSize * 0.4}px`;
                          dot.style.marginTop = `${rawFontSize * 0.45}px`;
                          dot.style.borderRadius = "50%";
                          dot.style.backgroundColor = "currentColor";
                          iconDiv.appendChild(dot);
                          
                          const textSpan = document.createElement("span");
                          textSpan.innerText = line;
                          textSpan.style.whiteSpace = "pre-wrap";
                          textSpan.style.textAlign = b.textAlign || b.align || "left";
                          
                          lineDiv.appendChild(iconDiv);
                          lineDiv.appendChild(textSpan);
                          textBlock.appendChild(lineDiv);
                        });
                      } else if (b.type === "divider") {
                        const lineDiv = document.createElement("div");
                        lineDiv.style.width = "100%";
                        lineDiv.style.height = `${b.borderWidth || 4}px`;
                        lineDiv.style.backgroundColor = b.color || "#e5e7eb";
                        lineDiv.style.borderRadius = "9999px";
                        textBlock.appendChild(lineDiv);
                      } else {
                        const rawSpan = document.createElement("span");
                        rawSpan.innerHTML = (b.text || "").replace(/\n/g, "<br/>");
                        rawSpan.style.whiteSpace = "pre-wrap";
                        rawSpan.style.wordBreak = "break-word";
                        rawSpan.style.maxWidth = "100%";
                        textBlock.appendChild(rawSpan);
                      }
                      
                      bakeContainer.appendChild(textBlock);
                    });

                    if (layout.enableFooter) {
                      const footer = document.createElement("div");
                      footer.style.position = "absolute";
                      footer.style.bottom = "0";
                      footer.style.left = "0";
                      footer.style.right = "0";
                      footer.style.display = "flex";
                      footer.style.alignItems = "center";
                      footer.style.justifyContent = "space-between";
                      footer.style.padding = "0 40px";
                      footer.style.height = `${layout.footerHeight || 10}%`;
                      footer.style.backgroundColor = layout.footerBgColor || "transparent";
                      footer.style.color = layout.footerTextColor || "#666666";
                      footer.style.fontSize = "26px";
                      footer.style.fontWeight = "bold";
                      footer.style.zIndex = "10";
                      
                      const leftLabel = document.createElement("span");
                      leftLabel.innerText = (layout.footerText && !layout.footerText.includes('[LOREM') && !layout.footerText.includes('FOOTER')) ? layout.footerText : (brand?.companyName || "");
                      footer.appendChild(leftLabel);

                      if (layout.footerShowPageNum && postFormat === 'carousel' && (totalSlides ?? 5) > 1) {
                        const pageLabel = document.createElement("span");
                        pageLabel.innerText = "01 / 01";
                        footer.appendChild(pageLabel);
                      }
                      bakeContainer.appendChild(footer);
                    }

                    document.body.appendChild(bakeContainer);
                    import("html2canvas").then(({ default: html2canvas }) => {
                      html2canvas(bakeContainer, {
                        useCORS: true,
                        allowTaint: true,
                        scale: 1,
                        backgroundColor: null,
                        logging: false
                      }).then(canvas => {
                        const link = document.createElement("a");
                        link.download = `slide-export-${slide.id || "render"}.png`;
                        link.href = canvas.toDataURL("image/png");
                        link.click();
                        document.body.removeChild(bakeContainer);
                      });
                    }).catch(err => {
                      console.error("Html2canvas import failed:", err);
                      document.body.removeChild(bakeContainer);
                    });
                  });
                }}
                className="p-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-900 rounded-xl flex items-center gap-1.5 text-xs font-bold transition-all shadow-sm active:scale-[0.98]"
                title="Bake and download high resolution master slide"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Export Image</span>
              </button>
            </div>

            {/* Canvas Mode Selection */}
            <div className="w-full max-w-[400px] bg-gray-100 p-1 rounded-xl border border-gray-200 shadow-inner flex mb-4 shrink-0 transition-all">
              <button
                type="button"
                onClick={() => {
                  updateLayout({ 
                    canvasEditingMode: "original",
                    enableContentPlaque: false // Disable by default
                  });
                }}
                className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  (layout.canvasEditingMode || "original") === "original"
                    ? "bg-white text-indigo-700 shadow-sm font-black"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                <span>Original AI Post</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const bgImage = slide.backgroundSliceUrl || slide.url || slide.thumbnailUrl || "";
                  updateLayout({ 
                    canvasEditingMode: "overlay",
                    backgroundType: bgImage ? "image" : "solid",
                    backgroundImage: bgImage,
                    enableContentPlaque: true // Enable by default for text editable layouts
                  });
                }}
                className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  (layout.canvasEditingMode || "original") === "overlay"
                    ? "bg-white text-emerald-600 shadow-sm font-black"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <Layers className="w-3.5 h-3.5 text-emerald-500" />
                <span>Editable Overlays</span>
              </button>
            </div>

            {/* Visual canvas layout sandbox matches requested look */}
            <div className="w-full max-w-[400px] flex items-center justify-center p-6 bg-white border border-gray-200 rounded-3xl shadow-lg relative shrink-0">
              <div 
                className="relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 select-none flex flex-col justify-between shadow-inner"
                style={{ aspectRatio: is916 ? "9/16" : "1/1" }}
              >
                <div 
                  ref={containerRef}
                  className="absolute inset-0 flex flex-col justify-between overflow-hidden select-none @container" 
                  style={{ ...bgStyle, containerType: "inline-size" }}
                >
                  {/* Snap Guides */}
                  {!isOriginalMode && snapGuide.x !== null && (
                    <div
                      className="absolute top-0 bottom-0 border-l border-blue-500 border-dashed z-50 pointer-events-none text-[8px] flex items-center justify-start text-blue-500"
                      style={{ left: `${snapGuide.x}%` }}
                    >
                      <span className="bg-white/80 border border-blue-200 rounded px-1 -ml-3 leading-none pointer-events-none">X: {snapGuide.x.toFixed(1)}%</span>
                    </div>
                  )}
                  {!isOriginalMode && snapGuide.y !== null && (
                    <div
                      className="absolute left-0 right-0 border-t border-blue-500 border-dashed z-50 pointer-events-none text-[8px] flex justify-center items-start text-blue-500"
                      style={{ top: `${snapGuide.y}%` }}
                    >
                      <span className="bg-white/80 border border-blue-200 rounded px-1 -mt-3.5 leading-none pointer-events-none font-mono">Y: {snapGuide.y.toFixed(1)}%</span>
                    </div>
                  )}
                  
                  {/* Content Plaque overlay */}
                  {!isOriginalMode && layout.enableContentPlaque && (
                    <div 
                      className={`absolute border cursor-move transition-all ${draggingPlaque ? "border-emerald-500 ring-2 ring-emerald-500/10" : "border-black/5 hover:border-gray-400"}`}
                      style={{
                        top: `${layout.plaqueTop ?? 15}%`,
                        left: `${layout.plaqueLeft ?? 10}%`,
                        width: `${layout.plaqueWidth ?? 80}%`,
                        height: `${layout.plaqueHeight ?? 70}%`,
                        backgroundColor: layout.plaqueBgColor || layout.plaqueColor || "#FFFFFF",
                        opacity: (layout.plaqueOpacity ?? 100) / 100,
                        borderRadius: `${layout.plaqueBorderRadius ?? 16}px`,
                        backdropFilter: layout.plaqueStyle === "glass" || layout.plaqueStyle === "floating-modern" ? "blur(16px)" : undefined,
                        boxShadow: layout.plaqueStyle === "glass" || layout.plaqueStyle === "floating-modern" || layout.plaqueStyle === "split-left" || layout.plaqueStyle === "split-right" ? "0 10px 30px rgba(0,0,0,0.08)" : (layout.plaqueStyle === "solid-shadow" ? "0 20px 40px rgba(0,0,0,0.12)" : "none"),
                        border: layout.plaqueStyle === "glass" ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(0,0,0,0.05)",
                        zIndex: 1
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDraggingPlaque({
                          offsetX: e.clientX - rect.left,
                          offsetY: e.clientY - rect.top,
                          type: "move"
                        });
                      }}
                    >
                      {/* Resize handle */}
                      <div 
                        className="absolute right-0 bottom-0 w-3.5 h-3.5 bg-emerald-500 hover:bg-emerald-400 rounded-tl cursor-se-resize flex items-center justify-center z-20"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setDraggingPlaque({
                            offsetX: 0,
                            offsetY: 0,
                            type: "resize"
                          });
                        }}
                      >
                        <span className="text-[6px] text-white font-sans font-bold">◲</span>
                      </div>
                    </div>
                  )}

                  {/* Header overlay */}
                  {!isOriginalMode && layout.enableHeader && (
                    <div 
                      className="absolute left-0 right-0 px-4 flex items-center justify-between"
                      style={{
                        top: 0,
                        height: `${layout.headerHeight || 10}%`,
                        backgroundColor: layout.headerBgColor || "transparent",
                        color: layout.headerTextColor || "#333333",
                        fontSize: `${layout.headerFontSize || 12}px`,
                        borderBottom: layout.headerStyle === "bordered" ? "1px solid rgba(0,0,0,0.08)" : layout.headerStyle === "accent-bar" ? `2px solid ${layout.headerTextColor || "#333"}` : "none",
                        backdropFilter: layout.headerStyle === "glass" ? "blur(12px)" : undefined,
                        zIndex: 10
                      }}
                    >
                      <span className="font-sans font-bold tracking-wider opacity-0">[HIDDEN HEADER TEXT]</span>
                      {layout.headerShowLogo && brand?.logoUrl && (
                        <img crossOrigin="anonymous" src={brand.logoUrl} alt="Logo" className="h-4 w-auto object-contain" referrerPolicy="no-referrer" />
                      )}
                    </div>
                  )}

                  {/* Corporate logo positioning */}
                  {!isOriginalMode && layout.logoPosition !== "none" && layout.logoPosition !== "header" && layout.logoPosition !== "footer" && brand?.logoUrl && (
                    <div 
                      className={`absolute flex transition-all ${layout.logoPosition === "floating" ? "cursor-move" : ""} ${draggingLogo ? "border border-emerald-500 bg-emerald-500/5" : "border border-transparent hover:border-gray-400"}`}
                      style={{
                        left: layout.logoPosition === "floating"
                          ? `${layout.logoLeft || 5}%`
                          : (layout.logoAlignment || "left") === "left"
                            ? "5%"
                            : layout.logoAlignment === "center"
                              ? "42.5%"
                              : "80%",
                        top: layout.logoPosition === "floating"
                          ? `${layout.logoTop || 5}%`
                          : (layout.logoPosition || "content-top") === "content-top"
                            ? "5%"
                            : "85%",
                        width: `${layout.logoSize || 15}%`,
                        height: `auto`,
                        maxHeight: `${layout.logoSize || 15}%`,
                        justifyContent: (layout.logoAlignment || "left") === "center" ? "center" : (layout.logoAlignment || "left") === "right" ? "flex-end" : "flex-start",
                        alignItems: "center",
                        zIndex: 10
                      }}
                      onPointerDown={(e) => {
                        if (layout.logoPosition !== "floating") return;
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDraggingLogo({
                          offsetX: e.clientX - rect.left,
                          offsetY: e.clientY - rect.top,
                          type: "move"
                        });
                      }}
                    >
                      <img 
                        crossOrigin="anonymous"
                        src={brand.logoUrl} 
                        alt="Logo" 
                        style={{ maxHeight: "100%", maxWidth: "100%" }} 
                        className="object-contain pointer-events-none" 
                        referrerPolicy="no-referrer"
                      />
                      {/* Resize handle */}
                      {layout.logoPosition === "floating" && (
                        <div 
                          className="absolute right-0 bottom-0 w-3 h-3 bg-emerald-500 hover:bg-emerald-400 rounded-tl cursor-se-resize flex items-center justify-center z-20"
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            setDraggingLogo({
                              offsetX: 0,
                              offsetY: 0,
                              type: "resize"
                            });
                          }}
                        >
                          <span className="text-[6px] text-white font-sans font-bold">◲</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Custom Text layers (Draggable & Resizable on canvas) */}
                  {!isOriginalMode && (layout.boxes || []).map((box: any) => {
                    const isSelected = box.id === selectedBoxId;
                    const isIconList = box.type === "icon-list";
                    const justifyDir = isIconList ? "gap-2" : box.type === "heading" ? "justify-end" : "justify-center";
                    return (
                      <div
                        key={box.id}
                        className={`absolute pointer-events-auto flex flex-col overflow-hidden cursor-move transition-all border outline-none ${box.fontFamily || ""} ${isSelected ? "border-emerald-500 bg-emerald-500/5 ring-1 ring-emerald-500" : "border-transparent hover:border-gray-300 hover:bg-black/5"} ${justifyDir}`}
                        style={{
                          top: `${box.top}%`,
                          left: `${box.left}%`,
                          width: `${box.width}%`,
                          height: box.flowNaturally ? "auto" : `${box.height}%`,
                          minHeight: box.flowNaturally ? `${box.height}%` : undefined,
                          textAlign: box.textAlign || box.align || "left",
                          color: box.color || "#1A1A1A",
                          fontSize: box.fontSize && box.fontSize < 12 
                            ? `${box.fontSize}cqw` 
                            : `max(6px, ${(box.fontSize || 16) * 0.2}cqw)`,
                          fontWeight: box.fontWeight === "bold" ? 700 : box.fontWeight === "medium" ? 500 : 400,
                          alignItems: (box.textAlign || box.align) === "center" ? "center" : (box.textAlign || box.align) === "right" ? "flex-end" : "flex-start",
                          lineHeight: box.lineHeight ?? 1.1,
                          letterSpacing: box.letterSpacing ?? "-0.02em",
                          transform: `translate(${box.xOffset || 0}em, ${box.yOffset || 0}em)`,
                          textTransform: box.textTransform === "uppercase" ? "uppercase" : "none",
                          zIndex: isSelected ? 40 : 20
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setSelectedBoxId(box.id);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDragging({
                            id: box.id,
                            offsetX: e.clientX - rect.left,
                            offsetY: e.clientY - rect.top,
                            type: "move"
                          });
                        }}
                      >
                        {box.type === "logo" ? (
                          brand?.logoUrl ? (
                            <img 
                              crossOrigin="anonymous"
                              src={brand.logoUrl} 
                              alt="Logo" 
                              className="max-w-full max-h-full object-contain pointer-events-none mix-blend-multiply" 
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className="text-[10px] uppercase font-black tracking-widest opacity-50">Logo</span>
                          )
                        ) : isIconList ? (
                            (box.text || "")
                              .split("\n")
                              .filter(Boolean)
                              .map((line: string, i: number) => {
                                const renderIcon = () => {
                                  if (box.iconType === "check") {
                                    return (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0" style={{ width: "1.2em", height: "1.2em", marginTop: "0.1em" }}>
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    );
                                  } else if (box.iconType === "cross") {
                                    return (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0" style={{ width: "1.2em", height: "1.2em", marginTop: "0.1em" }}>
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    );
                                  } else if (box.iconType === "arrow") {
                                    return (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0" style={{ width: "1.2em", height: "1.2em", marginTop: "0.1em" }}>
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                      </svg>
                                    );
                                  } else if (box.iconType === "dot" || !box.iconType) {
                                    return (
                                      <span className="shrink-0 rounded-full bg-current" style={{ width: "0.4em", height: "0.4em", marginTop: "0.45em" }} />
                                    );
                                  }
                                  return (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0" style={{ width: "1.2em", height: "1.2em", marginTop: "0.1em" }}>
                                      <circle cx="12" cy="12" r="10" />
                                      <path d="M12 8v8M8 12h8" />
                                    </svg>
                                  );
                                };

                                return (
                                  <div key={i} className="flex flex-row items-center gap-3 w-full" style={{ justifyContent: (box.textAlign || box.align) === "center" ? "center" : (box.textAlign || box.align) === "right" ? "flex-end" : "flex-start", opacity: (box.opacity ?? 100) / 100 }}>
                                    <div style={{ color: box.iconColor || box.color || "#333", marginTop: "0.15em" }} className="flex inline-flex items-center justify-center pointer-events-none select-none shrink-0">
                                      {renderIcon()}
                                    </div>
                                    <div className="w-full flex-1 min-w-0">
                                      <AutoFitText
                                        text={line}
                                        box={{
                                          ...box,
                                          type: "icon-list",
                                          text: line,
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })
                        ) : box.type === "divider" ? (
                          <div className="w-full h-full flex items-center justify-center pointer-events-none">
                            <div
                              style={{
                                width: "100%",
                                height: `${box.borderWidth || 4}px`,
                                backgroundColor: box.color || "#e5e7eb",
                                borderRadius: "9999px",
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center transition-all overflow-hidden" style={{ width: "100%", height: "100%" }}>
                            <div className="min-w-0 flex-1 flex items-center h-full" style={{ justifyContent: (box.textAlign || box.align) === "center" ? "center" : (box.textAlign || box.align) === "right" ? "flex-end" : "flex-start", opacity: (box.opacity ?? 100) / 100 }}>
                              <AutoFitText text={box.text} box={box} />
                            </div>
                          </div>
                        )}
                        
                        {/* Resize handle */}
                        {isSelected && (
                          <div 
                            className="absolute right-0 bottom-0 w-3 h-3 bg-emerald-500 hover:bg-emerald-400 rounded-tl cursor-se-resize flex items-center justify-center z-50 pointer-events-auto"
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              setSelectedBoxId(box.id);
                              setDragging({
                                id: box.id,
                                offsetX: 0,
                                offsetY: 0,
                                type: "resize"
                              });
                            }}
                          >
                            <span className="text-[6px] text-white font-sans font-bold">◲</span>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Footer overlay */}
                  {!isOriginalMode && layout.enableFooter && (
                    <div 
                      className="absolute left-0 right-0 px-4 flex items-center justify-between"
                      style={{
                        bottom: 0,
                        height: `${layout.footerHeight || 10}%`,
                        backgroundColor: layout.footerBgColor || "transparent",
                        color: layout.footerTextColor || "#666666",
                        fontSize: "9px",
                        fontWeight: "bold",
                        zIndex: 10
                      }}
                    >
                      <span className="font-sans font-black opacity-90">{(layout.footerText && !layout.footerText.includes('[LOREM') && !layout.footerText.includes('FOOTER')) ? layout.footerText : (brand?.companyName || "")}</span>
                      {layout.footerShowPageNum && postFormat === 'carousel' && (totalSlides ?? 5) > 1 && (
                        <span className="font-mono font-bold opacity-70">01 / 01</span>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </div>

            {/* Column CTA Action Triggers */}
            <div className="w-full max-w-[400px] flex flex-col gap-3 shrink-0 pt-2 bg-transparent z-10 px-1">
              
              <button
                onClick={handleGenerateArtwork}
                disabled={isGenerating || loadingSlideId !== null}
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-750 text-white rounded-xl text-xs font-black tracking-wider uppercase transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 cursor-pointer active:scale-[0.98]"
              >
                {isGenerating || loadingSlideId !== null ? (
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                ) : (
                  <Sparkles className="w-4 h-4 text-emerald-300 fill-emerald-300 animate-pulse" />
                )}
                <span>Generate Post Artwork with Gemini</span>
              </button>

              <button
                onClick={() => onSave(layout)}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs tracking-wider uppercase rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
              >
                <Check className="w-4 h-4" />
                <span>Apply & Save Layout</span>
              </button>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};
